import {useEffect} from 'react';
import './ModalPopup.css';

const ModalPopup = (props) => {

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, { root:document.querySelector('modal-body') , threshold: 0.08 });
      document.querySelectorAll('.chapter, .closing').forEach(el => observer.observe(el));
  },[])

  return (
    <div class="modal-body">
    <nav class="modal-nav">
      <a class="modal-nav-logo" href="#"><span>R²</span> Route-Reservation Engine</a>
      <span class="nav-right">
        Orbiin / FDNY Overview   
        <span class="close-modal" onClick={props.handleModalClick}>✕</span>
      </span>
      
    </nav>

    <section class="hero">
      <p class="hero-eyebrow">Orbiin — New York City</p>
      <h1>A faster path.<br/>Before it's <em>needed.</em></h1>
      <p class="hero-sub">
        New York does not need another navigation app. It needs a smarter way to clear a path when seconds matter. R² is the engine that makes that possible.
      </p>
      <p class="scroll-hint">scroll to continue</p>
    </section>

    <main class="story">
      <article class="chapter">
        <p class="chapter-label">The Problem</p>
        <h2>Every second an emergency vehicle sits in traffic is a second the city fails someone.</h2>
        <p>
          Dense urban traffic is not simply an inconvenience. In an emergency, it is a structural risk. Drivers do not know a unit is approaching until it is already behind them. By then, the corridor is already congested, the reaction is rushed, and the delay is compounding.
        </p>
        <p>
          The problem is not driver behavior. The problem is <strong>information arriving too late.</strong>
        </p>
        <div class="callout">
          <p>R² is designed to solve this: not after the vehicle is blocked, but before it ever reaches the bottleneck.</p>
        </div>
      </article>

      <article class="chapter">
        <p class="chapter-label">The Platform</p>
        <h2>Software-first. No new hardware. No infrastructure overhaul.</h2>
        <p>
          R² combines dispatch and vehicle-location inputs with live roadway, congestion, and map data to identify the most viable emergency corridor in real time.
        </p>
        <p>
          It is designed to work with existing public-safety and mobility systems — no traffic-signal control required at launch, no equipment installed in civilian vehicles, no specialized training for drivers or dispatchers.
        </p>
        <p>
          In New York, <strong>a software-first pilot can begin now</strong> and grow into a broader traffic-priority stack as confidence in the system is established.
        </p>

        <ul class="bare-list">
          <li>Works above existing navigation apps — Google Maps, Waze, Apple Maps</li>
          <li>Citywide adaptive sectors monitor emergency movement in real time</li>
          <li>Integrates with dispatch and CAD systems without replacing them</li>
          <li>Expandable into V2X and signal-priority integrations where the City chooses</li>
        </ul>
      </article>

      <article class="chapter">
        <p class="chapter-label">How It Works</p>
        <h2>The corridor opens before the apparatus arrives.</h2>
        <p>
          When a unit is dispatched, R² begins scoring route options continuously across the city road graph. It predicts where civilian traffic is most likely to interfere — and issues early, localized guidance to nearby participating drivers so lane space begins to open <strong>before the apparatus reaches the bottleneck.</strong>
        </p>
        <div class="divider"></div>
        <p>
          The objective is not to control city traffic. The objective is to make the next best legal move happen earlier, more calmly, and with better information.
        </p>
        <div class="stat-row">
          <div class="stat-cell">
            <p class="stat-num">DETECT</p>
            <p>Active emergency movement identified at dispatch. Corridor analysis begins immediately.</p>
          </div>
          <div class="stat-cell">
            <p class="stat-num">PREDICT</p>
            <p>R² scores the road graph and identifies where civilian traffic will conflict with the route.</p>
          </div>
          <div class="stat-cell">
            <p class="stat-num">GUIDE</p>
            <p>Early, calm guidance reaches nearby drivers before the unit arrives — not after.</p>
          </div>
        </div>
      </article>

      <article class="chapter">
        <p class="chapter-label">Civilian Experience</p>
        <h2>Simple for the driver. Invisible until it matters.</h2>
        <p>
          Drivers do not need a new dashboard, new hardware, or specialized training. Orbiin is delivered through approved mobile and in-vehicle driving surfaces — a&nbsp;lightweight layer that runs quietly in the background.
        </p>
        <p>
          When R² identifies a nearby driver who can help open a corridor, that driver receives a brief, glanceable cue: move right, slow down, prepare to stop. <strong>No&nbsp;panic. No&nbsp;interruption. No&nbsp;information they do not need.</strong>
        </p>
        <ul class="bare-list">
          <li>Brief alerts — minimal visual load while driving</li>
          <li>No forced navigation replacement</li>
          <li>No requirement for active engagement while in motion</li>
          <li>Guidance disappears when it is no longer relevant</li>
        </ul>
      </article>

      <article class="chapter">
        <p class="chapter-label">Privacy & Security</p>
        <h2>Civilian drivers never see what they should not see.</h2>
        <p>
          Sensitive emergency information — EMS destination and dispatcher-only operational data — remains inside the secured R² coordination environment at all times.
        </p>
        <p>
          Civilian guidance is based on <strong>proximity, route conflict, and roadway state</strong> — nothing more. The architecture follows minimum-necessary data handling, de&#8209;identification where appropriate, and secured transmission and access controls from day one.
        </p>
      </article>

      <article class="chapter">
        <p class="chapter-label">Measured Impact</p>
        <h2>Evaluated like a mission system. Not a consumer app.</h2>
        <p>
          R² is not judged on downloads or engagement. It is judged on operational outcomes that FDNY and the City can verify and audit.
        </p>
        <div class="stat-row">
          <div class="stat-cell">
            <p class="stat-num">PRIMARY KPI</p>
            <p>Reduce dispatch-and-travel delay on eligible incidents while maintaining safety and minimizing false alerts.</p>
          </div>
          <div class="stat-cell">
            <p class="stat-num">SECONDARY</p>
            <p>Corridor-clearance rate, dispatcher usability score, privacy and security compliance.</p>
          </div>
          <div class="stat-cell">
            <p class="stat-num">PILOT SCOPE</p>
            <p>One borough. Measurable outcomes before the City commits to broader deployment.</p>
          </div>
        </div>
        <p>
          A one-borough pilot establishes whether the software meaningfully improves travel performance — on real incidents, in real New York traffic — before any further commitment is made.
        </p>
      </article>

      <div class="closing">
        <p class="chapter-label">Vision</p>
        <h2>This is not navigation.<br/>This is urban movement orchestration.</h2>
        <p>
          Orbiin transforms emergency movement from reactive navigation into coordinated citywide intelligence. The platform does not replace existing systems. It operates above them — synchronizing civilian traffic flow and emergency response movement at the same time.
        </p>
        <p>
          R² is designed for dense urban environments where seconds matter, road conditions change constantly, and emergency movement depends on the behavior of thousands of surrounding drivers.
        </p>
        <p>
          Every day that emergency vehicles sit in preventable traffic is a day the city accepts a cost it does not have to. New York has the density, the dispatch infrastructure, and the driver base to make R² work — <strong>and work now.</strong>
        </p>
        <p class="tag-line">— Real-time urban movement orchestration. Orbiin.</p>
      </div>
    </main>

    <footer>
      <span>Orbiin / R² Route-Reservation Engine</span>
      <span>New York City Pilot Overview</span>
    </footer>
  </div>
  )
}

export default ModalPopup;